import { Box, Typography, Divider, InputBase, IconButton } from '@mui/material';
import { FC, MouseEvent, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StringRecognizedLabel, RecognizedLabelsStructureType } from 'entities/Files/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import { getLabelSourceId } from 'components/file-editor-components/file-viewer/region-types';
import Collapse from 'components/collapse';

type LabelFieldsProps = {
	labels: RecognizedLabelsStructureType[];
	selectedSourceId?: string | null;
	onSelectSource?: (sourceId: string | null) => void;
	onContentChange?: (labelIndex: number, content: string) => void;
	onDeleteLabel?: (labelIndex: number) => void;
	drawMode?: boolean;
	pendingLabelTarget?: number | null;
	onLabelTargetClick?: (labelIndex: number) => void;
};

type LabelItemProps = {
	label: StringRecognizedLabel;
	labelIndex: number;
	sourceId: string;
	isSelected: boolean;
	isPendingTarget: boolean;
	isLast: boolean;
	drawMode?: boolean;
	onSelectSource?: (sourceId: string | null) => void;
	onContentChange?: (labelIndex: number, content: string) => void;
	onDeleteLabel?: (labelIndex: number) => void;
	onLabelTargetClick?: (labelIndex: number) => void;
};

const LabelItem = memo(function LabelItem({
	label,
	labelIndex,
	sourceId,
	isSelected,
	isPendingTarget,
	isLast,
	drawMode,
	onSelectSource,
	onContentChange,
	onDeleteLabel,
	onLabelTargetClick,
}: LabelItemProps) {
	const value = label?.assertionsData?.replaceVal || label?.content || '';
	const canLinkRegion = Boolean(drawMode && onLabelTargetClick && !label.boundingRegions?.length);

	const handleClick = () => {
		if (canLinkRegion) {
			onLabelTargetClick?.(labelIndex);
			return;
		}
		onSelectSource?.(isSelected ? null : sourceId);
	};

	const handleDelete = (event: MouseEvent) => {
		event.stopPropagation();
		onDeleteLabel?.(labelIndex);
	};

	return (
		<Box sx={{ py: 0.25 }} data-source-id={sourceId}>
			<Box
				onClick={handleClick}
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: 1,
					borderRadius: '10px',
					p: 1.25,
					cursor: canLinkRegion || onSelectSource ? 'pointer' : 'default',
					border: '1.5px solid',
					borderColor: isSelected
						? ColorPalette.royalBlue
						: isPendingTarget
						? ColorPalette.violetRed
						: 'transparent',
					backgroundColor: isSelected ? ColorPalette.zircon : 'transparent',
				}}
			>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Typography variant={'body2'} color={ColorPalette.astronaut}>
						{label.label}
					</Typography>
					{onContentChange ? (
						<InputBase
							value={value}
							onChange={(event) => onContentChange(labelIndex, event.target.value)}
							multiline
							sx={{ width: '100%', fontSize: 'inherit', color: ColorPalette.baliHai }}
							inputProps={{ style: { padding: 0 } }}
							disabled={Boolean(label?.assertionsData?.replaceVal || label?.autocomplete)}
						/>
					) : (
						<Typography variant={'body2'} color={ColorPalette.baliHai}>
							{value}
						</Typography>
					)}
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					{label.autocomplete && (
						<Icon
							id={'replace_value'}
							color={ColorPalette.astronaut}
							style={{ width: '18px', height: '18px' }}
						/>
					)}

					{onDeleteLabel && (
						<IconButton onClick={handleDelete} size={'small'} sx={{ padding: '2px' }}>
							<Icon
								id={'close_round'}
								color={ColorPalette.violetRed}
								style={{ width: '18px', height: '18px' }}
							/>
						</IconButton>
					)}
				</Box>
			</Box>
			{!isLast && <Divider sx={{ marginTop: 0.5 }} />}
		</Box>
	);
});

const LabelFields: FC<LabelFieldsProps> = ({
	labels,
	selectedSourceId,
	onSelectSource,
	onContentChange,
	onDeleteLabel,
	drawMode,
	pendingLabelTarget,
	onLabelTargetClick,
}) => {
	const { t } = useTranslation();

	const stringLabels = useMemo(
		() =>
			labels
				.map((label, labelIndex) => ({ label, labelIndex, sourceId: getLabelSourceId(labelIndex) }))
				.filter(
					(
						entry,
					): entry is { label: StringRecognizedLabel; labelIndex: number; sourceId: string } =>
						entry.label.type === 'string',
				),
		[labels],
	);

	const isOwnSelection = Boolean(selectedSourceId?.startsWith('label-'));

	useEffect(() => {
		if (!isOwnSelection) return;
		const timeout = setTimeout(() => {
			document
				.querySelector(`[data-source-id="${CSS.escape(selectedSourceId as string)}"]`)
				?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}, 300);
		return () => clearTimeout(timeout);
	}, [isOwnSelection, selectedSourceId]);

	return (
		<Collapse
			label={`${t('files.details.labels')} ${stringLabels.length}`}
			expandSignal={isOwnSelection ? selectedSourceId : undefined}
		>
			{stringLabels.map((item, index) => (
				<LabelItem
					key={item.sourceId}
					label={item.label}
					labelIndex={item.labelIndex}
					sourceId={item.sourceId}
					isSelected={selectedSourceId === item.sourceId}
					isPendingTarget={pendingLabelTarget === item.labelIndex}
					isLast={index === stringLabels.length - 1}
					drawMode={drawMode}
					onSelectSource={onSelectSource}
					onContentChange={onContentChange}
					onDeleteLabel={onDeleteLabel}
					onLabelTargetClick={onLabelTargetClick}
				/>
			))}
		</Collapse>
	);
};

export default LabelFields;
